import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faRightLeft } from '@fortawesome/free-solid-svg-icons';

import { IReservation, IUserWalletRewardItem } from '@share/common-types';
import { getSavedProperties, ILoginState, IMenuState, IMyProfileState, ISavedPropertiesState } from '@share/store/slices';
import { ReservationsAPI } from '@share/api';
import { useCertificatesWallet } from '@share/components';
import { FunctionalRootState, isDisplayCertificates, RootState } from '@share/utils';
import { Routes, RENEWAL_AGENCY_EXCLUDE_RENEWAL, RENEWAL_AGENCY_INCLUDE_RENEWAL } from '@share/constants';

import { getAccountGuides } from '../../guides';
import type { AccountSectionId, IAccountSection } from '../../types';
import { useAccountBalances } from '../../balances';
import { getAccountSectionPath, parseApiDate } from '../../utils';
import { AccountGuides } from './guides';
import { ACTIVE_CERTIFICATE_STATUSES } from '../certificates';
import { NextTrips } from './next-trips';
import { PaymentSnapshot } from './payment-snapshot';

import styles from './styles.module.scss';

const UPCOMING_PAGE_SIZE = 50;
const MAX_PREVIEW_TRIPS = 5;
const MAX_RECENT_ACTIVITY = 4;

interface IProps {
  sections: IAccountSection[];
  accountUsername: string;
}

interface IUpcomingState {
  loading: boolean;
  failed: boolean;
  /** Bookings checking in today or later, soonest first; `hasMore` when the page may have cut the list short. */
  trips: IReservation[];
  hasMore: boolean;
}

type BalanceCardKey = 'commissions' | 'wallet' | 'points';
const BALANCE_CARD_KEYS: BalanceCardKey[] = ['commissions', 'wallet', 'points'];
const BALANCE_CARD_STORAGE_KEY = 'accountHub.balanceCard';

interface IBalanceCard {
  key: BalanceCardKey;
  label: string;
  peekValue: string;
  peekClassName: string;
  element: React.ReactElement;
}

// Remembers which balance card the member keeps in front, per device. Storage can be unavailable
// (private mode, blocked site data); the stack then just starts with its first card.
const readBalanceCardChoice = (): BalanceCardKey | null => {
  try {
    const stored = window.localStorage.getItem(BALANCE_CARD_STORAGE_KEY) as BalanceCardKey | null;
    return stored && BALANCE_CARD_KEYS.includes(stored) ? stored : null;
  } catch (error) {
    console.warn('Account overview: could not read the balance card choice', error);
    return null;
  }
};

const saveBalanceCardChoice = (value: BalanceCardKey): void => {
  try {
    window.localStorage.setItem(BALANCE_CARD_STORAGE_KEY, value);
  } catch (error) {
    console.warn('Account overview: could not save the balance card choice', error);
  }
};

const getUpcomingTrips = (items: IReservation[]): IReservation[] => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return items
    .map(item => ({ item, checkIn: parseApiDate(item.checkIn) }))
    .filter(({ checkIn }) => !!checkIn && checkIn >= today)
    .sort((a, b) => (a.checkIn as Date).getTime() - (b.checkIn as Date).getTime())
    .map(({ item }) => item);
};

export const AccountOverview = ({ sections, accountUsername }: IProps): React.ReactElement => {
  const intl = useIntl();
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, Action>>();

  const loginStore: ILoginState = useSelector((state: FunctionalRootState) => state.loginStore);
  const menuStore: IMenuState = useSelector((state: FunctionalRootState) => state.navigationMenuStore);
  const myProfileStore: IMyProfileState = useSelector((state: FunctionalRootState) => state.myProfileStore);
  const savedPropertiesStore: ISavedPropertiesState = useSelector((state: FunctionalRootState) => state.savedPropertiesStore);

  const { account, user, userWalletData, userWalletReward } = loginStore;
  const profile = myProfileStore?.result?.profile;

  const sectionById = useMemo(() => {
    const map: Partial<Record<AccountSectionId, IAccountSection>> = {};
    sections.forEach(section => { map[section.id] = section; });
    return map;
  }, [sections]);

  const showCertificates = isDisplayCertificates(account);
  const showSaved = !!sectionById.saved;

  const [balanceCardChoice, setBalanceCardChoice] = useState<BalanceCardKey | null>(readBalanceCardChoice);
  const showBalanceCard = (key: BalanceCardKey) => {
    setBalanceCardChoice(key);
    saveBalanceCardChoice(key);
  };

  const [upcoming, setUpcoming] = useState<IUpcomingState>({ loading: true, failed: false, trips: [], hasMore: false });

  const { certificates, loading: certificatesLoading } = useCertificatesWallet({
    statuses: ACTIVE_CERTIFICATE_STATUSES,
    autoFetch: showCertificates && !!user?.email,
    email: user?.email,
    organizationId: user?.userAdditionalData?.organizationId,
  });

  // Tracks our own request, so the tile never shows the empty store as "0" before (or instead of) real data.
  const [savedRequested, setSavedRequested] = useState(false);

  useEffect(() => {
    if (showSaved && !loginStore.loading) {
      // The thunk flips `loading` on synchronously, so there's no render with stale data in between.
      dispatch(getSavedProperties('created'));
      setSavedRequested(true);
    }
  }, [showSaved, loginStore.loading]);

  useEffect(() => {
    let isActive = true;

    ReservationsAPI.getReservations({
      bookingId: 0,
      propertyName: '',
      leadName: '',
      bookingType: '',
      status: 'active',
      page: 1,
      pageSize: UPCOMING_PAGE_SIZE,
      // Latest check-in first, so future trips are on this page. "active" also includes past stays.
      orderBy: 'checkIn',
      sortOrder: 'desc',
    })
      .then(({ data }) => {
        if (!isActive) {
          return;
        }
        if (data?.status) {
          setUpcoming({ loading: false, failed: true, trips: [], hasMore: false });
          return;
        }
        const trips = getUpcomingTrips(data?.items || []);
        setUpcoming({ loading: false, failed: false, trips, hasMore: trips.length === UPCOMING_PAGE_SIZE });
      })
      .catch((error: unknown) => {
        console.error('Account overview: upcoming trips request failed', error);
        if (isActive) {
          setUpcoming({ loading: false, failed: true, trips: [], hasMore: false });
        }
      });

    return () => {
      isActive = false;
    };
  }, []);

  const renewalAgency = menuStore?.items?.renewalAgency as string;
  const isRenewalMember = RENEWAL_AGENCY_INCLUDE_RENEWAL.includes(renewalAgency) && !RENEWAL_AGENCY_EXCLUDE_RENEWAL.includes(renewalAgency);
  const showRenewal = isRenewalMember && !!profile?.expirationDate && !!sectionById.profile;

  // Wallet, leisure credits and agent commissions: shared with the account menu (see balances.ts).
  const {
    showWallet,
    formatWallet,
    points,
    pointsLabel,
    showCommissions,
    commissions: commissionsData,
    commissionsRange,
    formatUsd,
  } = useAccountBalances();
  const showPointsCard = points !== null;
  const commissionsSection = sectionById.reports;
  const showCommissionsCard = !!commissionsSection && showCommissions;

  const hasBalanceCard = showWallet || showPointsCard || showCommissionsCard;

  // A wallet that has never received anything: show how it fills up instead of a row of zeros.
  const isWalletEmpty = !userWalletData?.balance && !userWalletData?.pendingBalance && !userWalletData?.usedBalance && !userWalletData?.expiredBalance;

  // Rewards are the only per-entry wallet history the API exposes today (tenants with the rewards wallet).
  const rewards: IUserWalletRewardItem[] = account?.enableWalletReward ? (userWalletReward?.allRewards || []) : [];
  const rewardCurrency = userWalletReward?.currency || 'USD';
  const formatMoney = (value: number | undefined) =>
    intl.formatNumber(value || 0, { style: 'currency', currency: rewardCurrency });

  const currentYear = new Date().getFullYear();
  const earnedThisYear = rewards
    .filter(reward => parseApiDate(reward.earnedDate)?.getFullYear() === currentYear)
    .reduce((total, reward) => total + (reward.rewardValue || 0), 0);

  const recentActivity = [...rewards]
    .sort((a, b) => (parseApiDate(b.earnedDate)?.getTime() || 0) - (parseApiDate(a.earnedDate)?.getTime() || 0))
    .slice(0, MAX_RECENT_ACTIVITY);

  const formatDate = (value: string | null | undefined): string => {
    const date = parseApiDate(value);
    return date ? intl.formatDate(date, { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  };

  const memberName = [profile?.firstName || user?.firstName, profile?.lastName || user?.lastName].filter(Boolean).join(' ');
  const memberId = String(menuStore?.items?.rsiId || user?.keyid || '');

  const paymentSection = sectionById.payment;
  const guides = getAccountGuides({ sections, hasWallet: showWallet });
  const bookingsSection = sectionById.bookings || sectionById.history;
  const bookingsPath = bookingsSection ? getAccountSectionPath(accountUsername, bookingsSection) : null;

  const stats: { key: string; value: string; label: string; to?: string }[] = [];
  if (!upcoming.loading && !upcoming.failed) {
    stats.push({
      key: 'upcoming',
      value: upcoming.hasMore ? `${upcoming.trips.length}+` : String(upcoming.trips.length),
      label: intl.formatMessage({ id: 'my.account.hub.stats.upcoming' }),
      to: bookingsPath || undefined,
    });
  }
  if (showCertificates && !certificatesLoading) {
    stats.push({
      key: 'certificates',
      // The store is shared with the header wallet drawer, which loads every status, so count here.
      value: String((certificates || []).filter(item => ACTIVE_CERTIFICATE_STATUSES.includes(item.status)).length),
      label: intl.formatMessage({ id: 'my.account.hub.stats.certificates' }),
      to: sectionById.certificates ? getAccountSectionPath(accountUsername, sectionById.certificates) : `/${accountUsername}${Routes.Certificates}`,
    });
  }
  if (showSaved && savedRequested && !savedPropertiesStore?.loading && !savedPropertiesStore?.error) {
    stats.push({
      key: 'saved',
      value: String(savedPropertiesStore?.properties?.length || 0),
      label: intl.formatMessage({ id: 'my.account.hub.stats.saved' }),
      to: getAccountSectionPath(accountUsername, sectionById.saved as IAccountSection),
    });
  }
  if (rewards.length) {
    stats.push({
      key: 'earned',
      value: formatMoney(earnedThisYear),
      label: intl.formatMessage({ id: 'my.account.hub.stats.earned' }),
      to: `/${accountUsername}${Routes.Rewards}`,
    });
  }

  const walletCard = showWallet ? (
    <section className={styles.walletCard} aria-labelledby="account-hub-wallet-title">
      <div className={styles.walletHeader}>
        <span id="account-hub-wallet-title" className={styles.walletLabel}>
          {intl.formatMessage({ id: 'my.account.hub.wallet.title' })}
        </span>
        {memberName || memberId ? (
          <span className={styles.walletMember}>
            {memberName}
            {memberName && memberId ? ' · ' : ''}
            {memberId ? intl.formatMessage({ id: 'my.account.hub.wallet.member_id' }, { id: memberId }) : ''}
          </span>
        ) : null}
      </div>
      <div className={styles.walletBalance}>{formatWallet(userWalletData?.balance)}</div>
      {isWalletEmpty ? (
        <p className={styles.walletEmpty}>{intl.formatMessage({ id: 'my.account.hub.wallet.empty' })}</p>
      ) : (
        <dl className={styles.walletBreakdown}>
          <div>
            <dt>{intl.formatMessage({ id: 'my.account.hub.wallet.pending' })}</dt>
            <dd>{formatWallet(userWalletData?.pendingBalance)}</dd>
          </div>
          <div>
            <dt>{intl.formatMessage({ id: 'my.account.hub.wallet.used' })}</dt>
            <dd>{formatWallet(userWalletData?.usedBalance)}</dd>
          </div>
          <div>
            <dt>{intl.formatMessage({ id: 'my.account.hub.wallet.expired' })}</dt>
            <dd>{formatWallet(userWalletData?.expiredBalance)}</dd>
          </div>
        </dl>
      )}
      {account?.enableWalletReward ? (
        <div className={styles.walletActions}>
          <Link to={`/${accountUsername}${Routes.Rewards}`} className={styles.walletButton}>
            {intl.formatMessage({ id: 'my.account.hub.wallet.view_rewards' })}
          </Link>
        </div>
      ) : null}
    </section>
  ) : null;

  const pointsCard = showPointsCard ? (
    <section className={`${styles.walletCard} ${styles.pointsCard}`} aria-labelledby="account-hub-points-title">
      <div className={styles.walletHeader}>
        <span id="account-hub-points-title" className={styles.walletLabel}>{pointsLabel}</span>
        {memberName || memberId ? (
          <span className={styles.walletMember}>
            {memberName}
            {memberName && memberId ? ' · ' : ''}
            {memberId ? intl.formatMessage({ id: 'my.account.hub.wallet.member_id' }, { id: memberId }) : ''}
          </span>
        ) : null}
      </div>
      <div className={styles.walletBalance}>{intl.formatNumber(points as number)}</div>
      <p className={styles.walletEmpty}>
        {intl.formatMessage({ id: points ? 'my.account.hub.points.body' : 'my.account.hub.points.empty' })}
      </p>
    </section>
  ) : null;

  const commissionsCard = showCommissionsCard ? (
    <section className={`${styles.walletCard} ${styles.commissionsCard}`} aria-labelledby="account-hub-commissions-card-title">
      <div className={styles.walletHeader}>
        <span id="account-hub-commissions-card-title" className={styles.walletLabel}>
          {intl.formatMessage({ id: 'my.account.hub.commissions.card_title' }, { range: commissionsRange })}
        </span>
        {memberName || memberId ? (
          <span className={styles.walletMember}>
            {memberName}
            {memberName && memberId ? ' · ' : ''}
            {memberId ? intl.formatMessage({ id: 'my.account.hub.wallet.member_id' }, { id: memberId }) : ''}
          </span>
        ) : null}
      </div>
      <div className={styles.walletBalance}>
        {commissionsData ? formatUsd(commissionsData.commissions) : <span className={styles.walletPlaceholder}>—</span>}
      </div>
      <dl className={styles.walletBreakdown}>
        <div>
          <dt>{intl.formatMessage({ id: 'my.account.hub.commissions.pending' })}</dt>
          <dd>{commissionsData ? formatUsd(commissionsData.pendingCommissions) : '—'}</dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: 'my.account.hub.commissions.sales' })}</dt>
          <dd>{commissionsData ? formatUsd(commissionsData.sales) : '—'}</dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: 'my.account.hub.commissions.bookings' })}</dt>
          <dd>{commissionsData ? intl.formatNumber(commissionsData.confirmedBookings || 0) : '—'}</dd>
        </div>
      </dl>
      <div className={styles.walletActions}>
        <Link to={getAccountSectionPath(accountUsername, commissionsSection as IAccountSection)} className={styles.walletButton}>
          {intl.formatMessage({ id: 'my.account.hub.commissions.view_reports' })}
        </Link>
      </div>
    </section>
  ) : null;

  // Every balance the member has, in default order: commissions (agents), money wallet, credits.
  const balanceCards: IBalanceCard[] = [];
  if (commissionsCard) {
    balanceCards.push({
      key: 'commissions',
      label: intl.formatMessage({ id: 'my.account.hub.commissions.card_title' }, { range: commissionsRange }),
      peekValue: commissionsData ? formatUsd(commissionsData.commissions) : '—',
      peekClassName: styles.cardPeekCommissions,
      element: commissionsCard,
    });
  }
  if (walletCard) {
    balanceCards.push({
      key: 'wallet',
      label: intl.formatMessage({ id: 'my.account.hub.wallet.title' }),
      peekValue: formatWallet(userWalletData?.balance),
      peekClassName: styles.cardPeekWallet,
      element: walletCard,
    });
  }
  if (pointsCard) {
    balanceCards.push({
      key: 'points',
      label: pointsLabel,
      peekValue: intl.formatNumber(points as number),
      peekClassName: styles.cardPeekPoints,
      element: pointsCard,
    });
  }
  const frontCard = balanceCards.find(card => card.key === balanceCardChoice) || balanceCards[0];
  const backCards = balanceCards.filter(card => card !== frontCard);

  return (
    <div className={styles.overview}>
      {/* Renewal reminder first, above everything else on the Overview. */}
      {showRenewal ? (
        <div className={styles.renewal} role="status">
          <FontAwesomeIcon icon={faCalendarDays} className={styles.renewalIcon} aria-hidden="true" />
          <span className={styles.renewalText}>
            {intl.formatMessage({ id: 'my.account.hub.renewal.message' }, { date: profile?.expirationDate })}
          </span>
          <Link to={getAccountSectionPath(accountUsername, sectionById.profile as IAccountSection)} className={styles.renewalLink}>
            {intl.formatMessage({ id: 'my.account.hub.renewal.cta' })}
          </Link>
        </div>
      ) : null}
      {/* Without a wallet card, the next trip goes full width on top and the summary tiles sit beneath it. */}
      <div className={hasBalanceCard ? styles.topRow : `${styles.topRow} ${styles.topRowNoWallet}`}>
        <div className={styles.leftColumn}>
          {frontCard && backCards.length ? (
            <div className={styles.cardStack}>
              {/* Cards behind peek out above the front one, like cards in a wallet; tap one to bring it forward. */}
              {backCards.map(card => (
                <button
                  key={card.key}
                  type="button"
                  className={`${styles.cardPeek} ${card.peekClassName}`}
                  onClick={() => showBalanceCard(card.key)}
                  aria-label={intl.formatMessage({ id: 'my.account.hub.balance.switch_to' }, { name: card.label })}
                >
                  <span className={styles.cardPeekLabel}>{card.label}</span>
                  <span className={styles.cardPeekValue}>
                    {card.peekValue}
                    <FontAwesomeIcon icon={faRightLeft} aria-hidden="true" />
                  </span>
                </button>
              ))}
              <div key={frontCard.key} className={styles.cardFront}>{frontCard.element}</div>
            </div>
          ) : null}
          {frontCard && !backCards.length ? frontCard.element : null}

          {stats.length ? (
            <ul className={styles.stats} aria-label={intl.formatMessage({ id: 'my.account.hub.stats.title' })}>
              {stats.map(stat => (
                <li key={stat.key}>
                  {stat.to ? (
                    <Link to={stat.to} className={`${styles.stat} ${styles.statLink}`}>
                      <span className={styles.statValue}>{stat.value}</span>
                      <span className={styles.muted}>{stat.label}</span>
                    </Link>
                  ) : (
                    <div className={styles.stat}>
                      <span className={styles.statValue}>{stat.value}</span>
                      <span className={styles.muted}>{stat.label}</span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : null}

          {paymentSection && myProfileStore?.result?.userCards && !myProfileStore?.loadingCards ? (
            <PaymentSnapshot
              cards={myProfileStore.result.userCards}
              paymentPath={getAccountSectionPath(accountUsername, paymentSection)}
            />
          ) : null}
        </div>

        <NextTrips
          layout={hasBalanceCard ? 'vertical' : 'horizontal'}
          trips={upcoming.trips.slice(0, MAX_PREVIEW_TRIPS)}
          loading={upcoming.loading}
          failed={upcoming.failed}
          bookingsPath={bookingsPath}
          planPath={`/${accountUsername}`}
          showLeadName={showCommissionsCard}
        />
      </div>


      <div className={recentActivity.length ? styles.bottomRow : undefined}>
        {recentActivity.length ? (
          <section className={styles.card} aria-labelledby="account-hub-activity-title">
            <div className={styles.cardHeader}>
              <h2 id="account-hub-activity-title" className={styles.label}>
                {intl.formatMessage({ id: 'my.account.hub.activity.title' })}
              </h2>
              <Link to={`/${accountUsername}${Routes.Rewards}`} className={styles.cardLink}>
                {intl.formatMessage({ id: 'my.account.hub.activity.see_all' })}
              </Link>
            </div>
            <ul className={styles.activity}>
              {recentActivity.map((reward, index) => (
                <li key={reward.rewardId ?? index} className={styles.activityRow}>
                  <div>
                    <div className={styles.activityTitle}>
                      {intl.formatMessage(
                        { id: 'my.account.hub.activity.reward' },
                        { product: reward.bookingTypeName || reward.bookingType || '' },
                      )}
                    </div>
                    <div className={styles.activityMeta}>
                      {[formatDate(reward.earnedDate), reward.status].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <span className={styles.activityAmount}>+{formatMoney(reward.rewardValue)}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <AccountGuides guides={guides} sections={sections} accountUsername={accountUsername} />
      </div>
    </div>
  );
};
